/**
 * The deck's panel primitives, shared by BOTH halves of the app.
 *
 * WHY THIS FILE EXISTS. He said the internal pages were "still very far from the main design" while
 * holding a screenshot of Your notes, and he was right in a way the previous pass could not fix.
 * That pass added CHROME devices, a topbar rule and a sidebar seam and an active state, and Your
 * notes is a content surface none of them touch.
 *
 * The real gap was structural. The deck had `Module` and `SignalCard`; the internal pages had
 * invented `home-card` and `home-row`, which carry a title and a body and nothing else. Same design
 * tokens, so the same colours; different components, so a different product. No amount of CSS
 * closes a gap in SHAPE.
 *
 * So the primitives move here and both halves import them. Nothing about the deck changes: this is
 * a move, and the classes keep their `cdeck-` prefix precisely so no stylesheet has to move with
 * them.
 */

import type React from 'react';
import { Icon, type IconName } from './Icon.js';
// The component carries its own stylesheet, so it cannot arrive unstyled in a chunk that does
// not happen to include the deck. Vite dedupes the duplicate import.
import './commandDeck.css';

/** The hue vocabulary. Semantic, never decorative: see tokens.css. */
export type PanelHue = 'action' | 'waiting' | 'time' | 'health' | 'know' | 'intent' | 'ask';

export function Panel({
  id,
  icon,
  hue,
  name,
  caption,
  headline,
  sub,
  subTone,
  defaultOpen,
  children,
}: {
  /**
   * A stable anchor, so something else can send him here.
   *
   * Added because six of the eight rail links did nothing at all: they carried `target: ''` and
   * the handler read `if (it.target !== '') reveal(...)`. The file already argued that a row of
   * controls where nothing happens makes every other control on the board suspect, and then let
   * exactly that ship in the rail. These panels ARE the destinations, so they need names.
   */
  id?: string;
  icon: IconName;
  hue: PanelHue;
  name: string;
  caption: string;
  headline: string;
  sub: string;
  subTone?: 'warn' | 'crit' | 'ok';
  /**
   * Start open.
   *
   * The deck wants these SHUT: a module there is a summary with a number, and eight open panels is
   * the flat wall the deck exists to avoid. An internal page is the opposite case, because there
   * the list IS the content, and porting Your notes onto this component without the flag hid every
   * note behind a summary row and made the page look emptier than the card it replaced. Defaults to
   * the deck's behaviour so nothing there changes.
   */
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <details id={id} className="cdeck-module" data-hue={hue} open={defaultOpen}>
      <summary>
        <span className="cdeck-module-icon">
          <Icon name={icon} size={16} />
        </span>
        <span className="cdeck-module-id">
          <span className="cdeck-module-name">{name}</span>
          <span className="cdeck-module-caption">{caption}</span>
        </span>
        <span className="cdeck-module-nums">
          <span className="cdeck-module-headline">{headline}</span>
          <span className="cdeck-module-sub" data-tone={subTone}>
            {sub}
          </span>
        </span>
        <span className="cdeck-module-chev" aria-hidden="true">
          <Icon name="chevron" size={12} />
        </span>
      </summary>
      <div className="cdeck-module-body">{children}</div>
    </details>
  );
}
